
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';

import { get, post } from '../lib/api';
import { readAuthToken, writeAuthToken, clearAuthToken, isAdminStoreTab, requestAdminStoreEnd, hasOpenedAdminStores, clearOpenedAdminStores } from '../lib/authStorage';
import { getUserFromToken, getPortalUrlForRole, decodeJwt } from '../lib/auth';
import { useLocation } from 'react-router-dom';
import { GOOGLE_CLIENT_ID } from '../lib/googleConfig';
import { rememberLogin, rememberAuthName } from '../lib/lastLogin';

const AuthContext = createContext(null);


// ============================================================
// GOOGLE CONFIGURATION
// ============================================================



// This permission is required to read the user's
// Google Account phone number through the People API.
const GOOGLE_PHONE_SCOPE =
  'https://www.googleapis.com/auth/user.phonenumbers.read';


// ============================================================
// AUTH PROVIDER
// ============================================================

export const AuthProvider = ({
  children,
}) => {
  const location = useLocation();

  // ----------------------------------------------------------
  // USER
  // ----------------------------------------------------------

  const [user, setUser] = useState(() => {

    const storedToken =
      readAuthToken();

    return storedToken
      ? getUserFromToken(storedToken)
      : null;
  });


  // ----------------------------------------------------------
  // TOKEN
  // ----------------------------------------------------------

  const [token, setToken] =
    useState(() =>
      readAuthToken()
    );


  // ----------------------------------------------------------
  // LOADING
  // ----------------------------------------------------------

  const [loading, setLoading] =
    useState(true);

  useEffect(() => {
    if (!isAdminStoreTab() && user?.name) rememberAuthName(user.name);
  }, [user]);


  useEffect(() => {
    if (loading || !user || user.role === process.env.REACT_APP_PORTAL_ROLE) return;
    const openingVendorSetup = location.pathname === '/vendor-setup'
      || (location.pathname === '/auth' && location.state?.from === '/vendor-setup');
    if (user.role === 'CUSTOMER' && process.env.REACT_APP_PORTAL_ROLE === 'VENDOR' && openingVendorSetup) return;
    const destination = getPortalUrlForRole(user.role);
    if (destination && new URL(destination).origin !== window.location.origin) {
      window.location.replace(destination);
    }
  }, [loading, user, location.pathname, location.state]);

  const refreshUser = async () => {
    const response = await get('/auth/me');
    setUser(response.user);
    return response.user;
  };

  // ==========================================================
  // INITIALIZE AUTH
  // ==========================================================

  useEffect(() => {

    const initializeAuth = async () => {

      const storedToken =
        readAuthToken();


      if (!storedToken) {

        setUser(null);
        setToken(null);
        setLoading(false);

        return;
      }


      const decodedUser =
        getUserFromToken(
          storedToken
        );


      if (decodedUser) {

        setUser(decodedUser);
        try { const response = await get('/auth/me'); setUser(response.user); } catch { /* Keep the decoded session when refresh is unavailable. */ }
        setToken(storedToken);

      } else {

        clearAuthToken();

        setUser(null);
        setToken(null);
      }


      setLoading(false);
    };


    initializeAuth();

  }, []);


  // ==========================================================
  // PERSIST SESSION
  // ==========================================================

  const persistSession = useCallback(
    (
      sessionToken,
      sessionUser = null
    ) => {

      if (!sessionToken) {
        throw new Error(
          'No authentication token received.'
        );
      }


      const nextUser =
        sessionUser ||
        getUserFromToken(
          sessionToken
        );


      writeAuthToken(sessionToken);


      setToken(sessionToken);
      setUser(nextUser);


      return nextUser;
    },
    []
  );

  useEffect(() => {
    if (!token) return undefined;
    const expiresAt = decodeJwt(token)?.exp * 1000;
    if (!Number.isFinite(expiresAt)) return undefined;
    let timer;
    const checkExpiry = () => {
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        if (readAuthToken() === token) clearAuthToken();
        setToken(null); setUser(null);
      } else {
        timer = setTimeout(checkExpiry, Math.min(remaining, 2147483647));
      }
    };
    checkExpiry();
    return () => clearTimeout(timer);
  }, [token]);

  const loginWithMobile = async (phone, code, channel = 'phone') => {
    const response = await post('/auth/mobile/confirm', { phone, code, channel });
    if (!response?.success || !response.token) throw new Error(response?.message || 'Unable to sign in.');
    const user = persistSession(response.token, response.user || getUserFromToken(response.token));
    rememberLogin('PHONE', phone);
    return user;
  };
  const loginWithDeliveryOtp = async (destination, code, channel) => {
    const response = await post('/auth/delivery-otp/confirm', { destination, code, channel });
    if (!response?.success || !response.token || response.user?.role !== 'DELIVERY') throw new Error(response?.message || 'Unable to sign in as a rider.');
    return persistSession(response.token, response.user);
  };
  const confirmGooglePhone = async (challengeId, code) => {
    const response = await post('/auth/google/phone-confirm', { challengeId, code });
    if (!response?.success || !response.token) throw new Error(response?.message || 'Unable to sign in.');
    const user = persistSession(response.token, response.user);
    rememberLogin('GOOGLE');
    return user;
  };
  const confirmAccountUpdate = async details => {
    const response = await post('/customers/me/details/confirm', details);
    if (!response?.success || !response.token) throw new Error(response?.message || 'Unable to update account details.');
    persistSession(response.token, response.user);
    return response;
  };


  // ==========================================================
  // EMAIL + PASSWORD LOGIN
  // ==========================================================

  const login = async (
    email,
    password
  ) => {

    try {

      const response =
        await post(
          '/auth/login',
          {
            email,
            password,
          }
        );


      if (
        response?.success &&
        response?.token
      ) {

        rememberLogin('EMAIL');

        return persistSession(
          response.token,
          response.user ||
            getUserFromToken(
              response.token
            )
        );
      }


      throw new Error(
        response?.message ||
        'Login failed'
      );

    } catch (error) {

      console.error(
        'Login error:',
        error
      );

      throw error;
    }
  };


  // ==========================================================
  // REQUEST GOOGLE ACCESS TOKEN
  // ==========================================================
  //
  // This is separate from the Google ID credential.
  //
  // The ID credential identifies the Google user.
  //
  // The access token gives us permission to call
  // Google People API and retrieve the phone number.
  //
  // ==========================================================

  const requestGoogleAccessToken =
    useCallback(() => {

      return new Promise(
        (
          resolve,
          reject
        ) => {

          // -----------------------------------------------
          // Check Google Client ID
          // -----------------------------------------------

          if (!GOOGLE_CLIENT_ID) {

            reject(
              new Error(
                'REACT_APP_GOOGLE_CLIENT_ID is not configured.'
              )
            );

            return;
          }


          // -----------------------------------------------
          // Check Google Identity Services
          // -----------------------------------------------

          if (
            !window.google ||
            !window.google.accounts ||
            !window.google.accounts.oauth2
          ) {

            reject(
              new Error(
                'Google Identity Services has not loaded.'
              )
            );

            return;
          }


          try {

            const tokenClient =
              window.google.accounts.oauth2.initTokenClient(
                {
                  client_id:
                    GOOGLE_CLIENT_ID,

                  scope:
                    GOOGLE_PHONE_SCOPE,

                  callback: (
                    response
                  ) => {

                    if (
                      response?.access_token
                    ) {

                      resolve(
                        response.access_token
                      );

                    } else {

                      reject(
                        new Error(
                          response?.error_description ||
                          response?.error ||
                          'Google access token was not received.'
                        )
                      );
                    }
                  },
                }
              );


            // Open Google's authorization
            // / consent popup.
            tokenClient.requestAccessToken(
              {
                prompt: 'consent',
              }
            );

          } catch (error) {

            reject(error);
          }
        }
      );

    }, []);


  // ==========================================================
  // GOOGLE LOGIN
  // ==========================================================

  const loginWithGoogle = async ({
    credential,
    accessToken,
  } = {}) => {

    try {

      // ------------------------------------------------------
      // Google ID credential is required
      // ------------------------------------------------------

      if (!credential) {

        throw new Error(
          'Google authentication credential was not received.'
        );
      }


      // ------------------------------------------------------
      // Get OAuth access token
      // ------------------------------------------------------
      //
      // If the caller already supplied one, use it.
      //
      // Otherwise request one from Google with
      // user.phonenumbers.read.
      //
      // ------------------------------------------------------

      let googleAccessToken =
        accessToken || '';




      console.log(
        'Google access token received:',
        Boolean(
          googleAccessToken
        )
      );


      // ------------------------------------------------------
      // Send both tokens to backend
      // ------------------------------------------------------

      const response =
        await post(
          '/auth/google',
          {
            ...(process.env.REACT_APP_PORTAL_ROLE === 'VENDOR' ? { portal: 'VENDOR' } : {}),
            credential,

            accessToken:
              googleAccessToken,
          }
        );


      // ------------------------------------------------------
      // Login successful
      // ------------------------------------------------------

      if (response?.success && response.verificationRequired) return response;
      if (response?.success && response.token) rememberLogin('GOOGLE');

      if (
        response?.success &&
        response?.token
      ) {

        return persistSession(
          response.token,
          response.user ||
            getUserFromToken(
              response.token
            )
        );
      }


      throw new Error(
        response?.message ||
        'Google login failed'
      );

    } catch (error) {

      console.error(
        'Google login error:',
        error
      );

      throw error;
    }
  };


  // ==========================================================
  // FACEBOOK LOGIN
  // ==========================================================

  const loginWithFacebook =
    async (
      accessTokenOrPayload = {}
    ) => {

      try {

        const accessToken =
          typeof accessTokenOrPayload ===
          'string'

            ? accessTokenOrPayload

            : accessTokenOrPayload
                ?.accessToken;


        if (!accessToken) {

          throw new Error(
            'Facebook authentication token was not received.'
          );
        }


        const response =
          await post(
            '/auth/facebook',
            {
              ...(process.env.REACT_APP_PORTAL_ROLE === 'VENDOR' ? { portal: 'VENDOR' } : {}),
              accessToken,
            }
          );

        if (response?.success && response.token) rememberLogin('FACEBOOK');


        if (
          response?.success &&
          response?.token
        ) {

          return persistSession(
            response.token,
            response.user ||
              getUserFromToken(
                response.token
              )
          );
        }


        throw new Error(
          response?.message ||
          'Facebook login failed'
        );

      } catch (error) {

        console.error(
          'Facebook login error:',
          error
        );

        throw error;
      }
    };


  // ==========================================================
  // REGISTER
  // ==========================================================

  const register = async (
    payload
  ) => {

    try {

      const response =
        await post(
          '/auth/register',
          payload
        );


      /*
       * Do NOT save a token here.
       *
       * Registration creates the account and
       * sends the email verification link.
       */

      if (!response?.success) {

        throw new Error(
          response?.message ||
          'Registration failed'
        );
      }


      if (response.token) persistSession(response.token, response.user);
      return response;

    } catch (error) {

      console.error(
        'Registration error:',
        error
      );

      throw error;
    }
  };


  // ==========================================================
  // LOGOUT
  // ==========================================================

  const [logoutError, setLogoutError] = useState('');
  const logout = async () => {
    if (isAdminStoreTab()) { requestAdminStoreEnd(); return; }
    setLogoutError('');
    if (user?.role === 'ADMIN' && hasOpenedAdminStores()) {
      try { await post('/vendor-access/end-all'); clearOpenedAdminStores(); }
      catch { setLogoutError('Unable to end the open store sessions. Please retry logout.'); return; }
    }

    clearAuthToken();

    setToken(null);
    setUser(null);
  };


  // ==========================================================
  // CONTEXT
  // ==========================================================

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,

        refreshUser,
        // Normal authentication
        login,
        loginWithMobile,
        loginWithDeliveryOtp,
        confirmGooglePhone,
        confirmAccountUpdate,
        register,

        // Google
        loginWithGoogle,
        requestGoogleAccessToken,

        // Facebook
        loginWithFacebook,

        // Logout
        logout,
      }}
    >
      {logoutError && <p role="alert" className="border-b bg-red-50 p-3 text-sm text-red-700">{logoutError}</p>}
      {children}
    </AuthContext.Provider>
  );
};


// ============================================================
// USE AUTH HOOK
// ============================================================

export const useAuth = () => {

  const context =
    useContext(AuthContext);


  if (!context) {

    throw new Error(
      'useAuth must be used within an AuthProvider'
    );
  }


  return context;
};
